import { PkIcon, PkSpinner } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import ClubSearch, { SearchType } from '../club-search';

import Shield from './shield';

import { useTranslateFn } from '@shared/Translate';
import type { Team } from '@shared-types/match.types';

import * as classes from './styles.module.scss';

type H2HSelectorProps = {
  redirectUrl: string;
  competitionFolder: string;
  teams: string[];
};

const H2HSelector = ({ options }: { options: H2HSelectorProps }) => {
  const t = useTranslateFn();
  const [club1, setClub1] = useState(null);
  const [club2, setClub2] = useState(null);
  const [showSelection, setShowSelection] = useState(false);
  const currentClub = useRef<'club1' | 'club2'>(null);
  const clubLoaded = useRef(false);
  const fromClick = useRef(false);
  const { redirectUrl } = options;
  const baseUrl = redirectUrl.substring(0, redirectUrl.indexOf('{team1}'));

  const handleClickClub = (name: 'club1' | 'club2') => () => {
    setShowSelection(true);
    currentClub.current = name;
  };

  const handleClickItem = (club: Team) => {
    setShowSelection(false);

    if (club) {
      if (currentClub.current === 'club1') {
        setClub1(club);
      } else {
        setClub2(club);
      }
    }
    fromClick.current = true;
  };

  const handleCloseSelector = () => {
    setShowSelection(false);
  };

  const handleClubsLoad = (clubs: Team[]) => {
    if (clubLoaded.current) return;
    clubLoaded.current = true;

    const [team1, team2] = options.teams;

    const [newClub1, newClub2] = clubs.filter((club) => club.id === team1 || club.id === team2);

    if (!!team1 && !!newClub1) {
      setClub1(newClub1.id === team1 ? newClub1 : newClub2);
    }

    if (!!team2 && !!newClub2) {
      setClub2(newClub2.id === team2 ? newClub2 : newClub1);
    }

    fromClick.current = false;
  };

  const itShouldRedirect = useMemo(() => club1 && club2 && fromClick.current, [club1, club2, fromClick]);

  useEffect(() => {
    if (itShouldRedirect) {
      if (club1.id === club2.id) {
        setClub1(null);
        setClub2(null);
        if (window.location.pathname !== baseUrl) {
          window.location.pathname = baseUrl;
        }
      } else {
        window.location.pathname = redirectUrl.replace('{team1}', club1.id).replace('{team2}', club2.id);
      }
    }
  }, [club1, club2, itShouldRedirect, baseUrl, redirectUrl]);

  const newOptions = {
    ...options,
    type: SearchType.FULLSCREEN,
    placeholder: t('search'),
  };

  return (
    <>
      {itShouldRedirect && <PkSpinner className={classes.loader} type='landscape' />}
      <div className={classes.h2HSelectorContainer}>
        <Shield onClick={handleClickClub('club1')} club={club1} />
        <span className={classes.vs}>{t('history_h2h_versus')}</span>
        <Shield onClick={handleClickClub('club2')} club={club2} />
      </div>
      {showSelection && <div className={classes.selectionOverlay} onClick={handleCloseSelector} aria-hidden />}
      <div
        className={clsx({
          [classes.clubSearchContainer]: true,
          [classes.visible]: showSelection,
        })}
      >
        <div className={classes.clubSearchTitleContainer}>
          <div className={classes.clubSearchTitle}>
            <span>{t('history_h2h_select_club')}</span>
            <PkIcon
              size={16}
              name='close-web-icon'
              color='interaction-compl'
              className={classes.clubSearchTitleIcon}
              onClick={handleCloseSelector}
            />
          </div>
        </div>
        <ClubSearch options={newOptions} onItemClick={handleClickItem} onClubsLoad={handleClubsLoad} />
      </div>
    </>
  );
};

export default memo(H2HSelector);
